import { useCallback, useEffect, useState } from 'react';

import { getElementGrants } from '../services/elementGrants';

const IDLE = Object.freeze({ status: 'absent', grants: Object.freeze([]), inherited: null });

/**
 * The element rules that can govern a card's items in one collection
 * (KPR-509; services/elementGrants.js): the card's own rows and its ancestor
 * cards'. Asked for only with both ids — the card page passes them only for
 * a manager of the collection in hand — and read again on `reload()` after a
 * save. `inherited` is what the collection above gives each element (KPR-623;
 * services/elementGrants.js), or null when the API said nothing about it.
 *
 * `status`:
 *   'loading'  the first answer is on its way (a reload keeps 'ready' and the
 *              rows already shown, so the page does not flicker);
 *   'ready'    the feature is on and the caller manages: `grants` is usable;
 *   'absent'   no ids, an API without element rules, the switch off, or a
 *              caller who does not manage — the same to the UI: no door;
 *   'error'    the read failed; also no door (a door that may not work is
 *              worse than none — the next visit asks again).
 */
export default function useElementGrants(collectionId, cardId) {
	const [state, setState] = useState(IDLE);
	const [tick, setTick] = useState(0);

	useEffect(() => {
		if (!collectionId || !cardId) {
			setState(IDLE);
			return undefined;
		}
		let live = true;
		const key = `${collectionId}:${cardId}`;
		// A reload of the SAME card keeps what is shown; another card's rows
		// are never shown for this one, not even for a beat.
		setState(prev => (prev.status === 'ready' && prev.key === key ? prev : { status: 'loading', grants: [], inherited: null, key }));
		getElementGrants(collectionId, cardId)
			.then(result => {
				if (!live) return;
				setState(result ? { status: 'ready', grants: result.grants, inherited: result.inherited ?? null, key } : { ...IDLE, key });
			})
			.catch(() => {
				if (live) setState({ status: 'error', grants: [], inherited: null, key });
			});
		return () => { live = false; };
	}, [collectionId, cardId, tick]);

	const reload = useCallback(() => setTick(value => value + 1), []);

	// The render right after the ids change still holds the previous card's
	// answer (the effect has not run yet): it is not this card's, so it is
	// not offered — absent until the read for THESE ids lands.
	const current = collectionId && cardId && state.key === `${collectionId}:${cardId}`;
	if (!current) return { status: collectionId && cardId ? 'loading' : 'absent', grants: IDLE.grants, inherited: null, available: false, reload };
	return { status: state.status, grants: state.grants, inherited: state.inherited ?? null, available: state.status === 'ready', reload };
}
